import React, { useEffect, useState, UIEvent, useRef } from 'react'
import Button from 'library/atoms/Button'
import BottomBar from 'library/atoms/BottomBar'
import { H6 } from 'library/atoms/Typography'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  OutletPresenter,
  SchedulablePresenter,
  OptionalExtraPresenter
} from 'shared/presenters'
import { ON_SERVER } from 'shared/enums/environment'
import Price from 'shared/library/atoms/Price'
import { FormikHelpers, useFormikContext } from 'formik'
import Layout from 'library/atoms/Layout'
import { definedNotNull, RecursivePartial } from 'shared/presenters/presenter'
import { TimeSlotBookingPresenter } from 'shared/presenters/TimeSlotBookingPresenter'
import useActivityValues from 'state/useActivityValues'
import { AddABookingValues } from 'helpers/generateInitialValuesForActivity'
import Header from 'library/molecules/Header'
import Summary from 'library/molecules/Summary'
import useTranslation from 'next-translate/useTranslation'
import { BackBreadcrumb } from 'library/molecules/Breadcrumb'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Head from 'next/head'
import ValidationError from 'library/molecules/ValidationError'
import Flex from 'shared/library/atoms/Flex'
import FieldHolder, {
  FieldTitle,
  FieldHolderQuantityRow
} from 'library/atoms/FieldHolder'
import ContentRadio from 'library/atoms/RadioButton/ContentRadio'
import ContentCheckbox from 'library/atoms/Checkbox/ContentCheckbox'
import ModalField from 'library/molecules/ModalField'
import ActivityConfigModals, {
  ModalProps,
  MODAL_CONFIG,
  MODAL_IDS
} from './ActivityConfigModals'
import Text from 'library/atoms/Typography/Text'
import formatOptionalExtras, {
  formatChosenOptionalExtrasIntoInputs
} from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import hasChosenFieldOptions from 'helpers/hasChosenFieldOptions'
import Quantity from 'library/molecules/Quantity'
import useGetPriceForBookingActivity, {
  UseGetPriceForBookingActivity
} from 'state/useGetPriceForBookingActivity'
import useModal from 'shared/state/useModal'
import CoverImage from 'library/atoms/CoverImage'
import Spinner from 'library/atoms/Spinner'
import TimeSlot, {
  TimeSlotContent,
  TimeSlotTag
} from 'library/molecules/TimeSlot'
import Divider from 'shared/library/atoms/Divider'
import {
  Materialized_Bookability_By_Days,
  Maybe,
  Time_Slot_Bookings
} from 'shared/presenters/graphqlTypes'
import styled from '@emotion/styled'
import useRedirectUnauthenticatedOnSchedulableMembersOnly from 'state/useRedirectUnauthenticatedOnSchedulableMembersOnly'
import SchedulablePrice from 'shared/library/atoms/SchedulablePrice'
import calculateMaxAttendants from 'helpers/calculateMaxAttendants'
import { SchedulableTypeEnum } from 'shared/enums/schedulables'
import Icon from 'shared/library/atoms/Icon'
import PersonGoingInput from 'library/molecules/PersonGoingInput'
import GiftSummary from 'library/molecules/GiftSummary'
import useBasket from 'state/useBasket'
import determineOptionalExtrasFromActivityVoucher from 'helpers/determineOptionalExtrasFromActivityVoucher'
import VoucherBar from 'library/molecules/VoucherBar'
import { BottomBarContainer } from 'library/atoms/BottomBar/BottomBar'
import { AnimatedIcon } from 'shared/library/atoms/Icon'
import { rgba } from 'polished'
import useAddToBasket from 'state/useAddToBasket'
import { trackEvent } from 'shared/helpers/pageTrackerTools'
import { useRouter } from 'next/router'
import { useEnthusiast } from 'shared/state/useAuth'
import widgetRoutes from 'enums/routes'
import { Mutation_Root } from 'shared/presenters/graphqlTypes'

dayjs.extend(utc)

interface ActivityConfigProps
  extends Pick<
    UseGetPriceForBookingActivity,
    'totalPrice' | 'totalPriceLoading' | 'finalPricePence'
  > {
  disabled?: boolean
  activity: SchedulablePresenter
  outlet: OutletPresenter
  goToDetails: () => void
  goToRequest: () => void
  view?: string
}

const ActivityConfig = ({
  outlet,
  activity,
  goToDetails,
  goToRequest,
  finalPricePence,
  totalPrice,
  totalPriceLoading
}: ActivityConfigProps) => {
  const { values, setFieldValue } = useFormikContext<AddABookingValues>()
  const { t } = useTranslation('chargeable')
  const { basket, refetchBasket } = useBasket()
  const { push, query } = useRouter()
  const widgetSlug = query.widgetSlug as string
  const { authenticated } = useEnthusiast()

  const [showScrollButton, setShowScrollButton] = useState<boolean>(true)
  const [currentScrollY, setCurrentScrollY] = useState<number>(0)

  const basketPriceModifiers = basket?.baskets_price_modifiers?.[0]
  const creditVoucher = basketPriceModifiers?.credit_voucher
  const discountCode = basketPriceModifiers?.discount_code
  const participantsFieldHolder = useRef<HTMLElement>(null)
  const dateFieldHolder = useRef<HTMLElement>(null)
  const participatingFieldHolder = useRef<HTMLElement>(null)
  const optionalExtrasFieldHolder = useRef<HTMLElement>(null)
  const shoppingBasketEnabled = outlet?.feature_toggles?.shopping_basket_enabled

  const bookingFromActivityVoucher =
    basket?.getLineItemBookingFromActivityVoucher()

  const activityVoucherId = bookingFromActivityVoucher?.activity_voucher?.id

  const activityVoucherLineItemId = basket?.getActivityVoucherLineItemId()

  const code = basket?.getActivityVoucherCode()

  useRedirectUnauthenticatedOnSchedulableMembersOnly({
    schedulableIsMembersOnly: !!activity.members_only
  })

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    /**
     * Some funky calculations to determnine if we should show the scroll button
     * This is because we can't use the scrollHeight of the main element as it's
     * it's an element with overflow which causes some weirdness
     */
    const mainHeight = document.querySelector('[id=main]')?.scrollHeight || 0
    const navHeight = document.querySelector('nav')?.scrollHeight || 0
    const headerHeight = document.querySelector('header')?.scrollHeight || 0

    if (!mainHeight) return
    setCurrentScrollY(event.currentTarget.scrollTop)
    setShowScrollButton(
      mainHeight - window.innerHeight >
        currentScrollY - navHeight - headerHeight
    )
  }

  /**
   * Instantiating dayjs in the render method to allow us to mock the Date on test but using
   * the state to prevent instantiating a new dayjs object on each render which could
   * cause triggering to re-render infinitely.
   */
  const [currentDate] = useState(dayjs().utc(true))

  const {
    adultsGoing,
    childrenGoing,
    optionalExtra: desiredOptionalExtras,
    calendarSelection,
    organizerParticipating,
    visibleTimeSlots,
    timeSlot,
    exclusive,
    isGift
  } = values

  let fromTime = calendarSelection?.utc() ?? currentDate
  // If the selected time is in the past, use the current time
  if (fromTime < currentDate) fromTime = currentDate

  // This ensures that customers won't see stale (possibly unavailable) times
  useEffect(() => {
    if (!calendarSelection) return

    refetchTimes({
      from: (calendarSelection ?? fromTime).toISOString(),
      to: (calendarSelection ?? fromTime).endOf('day').toISOString(),
      schedulableId: activity.id
    })
    // TODO #6362 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarSelection])

  const {
    data: timeSlotData,
    loading: dateTimeLoading,
    error: dateTimeError,
    refetch: refetchTimes
  } = useQuery<{ time_slot_bookings: Time_Slot_Bookings[] }>(
    FETCH_ACTIVITY_TIMES,
    {
      variables: {
        from: (calendarSelection ?? fromTime).toISOString(),
        to: (calendarSelection ?? fromTime).endOf('day').toISOString(),
        schedulableId: activity.id
      },
      skip: ON_SERVER || !calendarSelection,
      ssr: ON_SERVER
    }
  )

  const timeSlotBookings = timeSlotData?.time_slot_bookings?.map(
    time => new TimeSlotBookingPresenter(time)
  )

  const updateTimeSlot = ({
    timeSlotId,
    setFieldValue
  }: {
    timeSlotId: string
  } & Pick<FormikHelpers<AddABookingValues>, 'setFieldValue'>) => {
    if (!timeSlotBookings) {
      return
    }

    const selectedTimeSlot = timeSlotBookings.find(dateTime => {
      return dateTime?.id === timeSlotId
    })

    selectedTimeSlot && setFieldValue('timeSlot', selectedTimeSlot)
  }

  useEffect(() => {
    if (!timeSlot.id) {
      return
    }
    updateTimeSlot({
      timeSlotId: timeSlot.id,
      setFieldValue
    })
    // TODO #6362 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlot.id])

  const adultsGoingQuantity = !bookingFromActivityVoucher
    ? adultsGoing
    : (bookingFromActivityVoucher.adultsGoing() ?? 0)

  const childrenGoingQuantity = !bookingFromActivityVoucher
    ? childrenGoing
    : (bookingFromActivityVoucher.childrenGoing() ?? 0)

  /**
   * increments visibleTimeSlots (we limit how many are visible on initial render for UX purposes)
   */
  const showLaterTimeSlots = () => {
    setFieldValue('visibleTimeSlots', visibleTimeSlots + 5)
  }

  const optionalExtraJSON = desiredOptionalExtras && activity.optional_extras
  const optionalExtraPresenter = optionalExtraJSON?.map(
    item => new OptionalExtraPresenter(item)
  )

  const chosenOptionalExtrasInputs = formatChosenOptionalExtrasIntoInputs(
    desiredOptionalExtras
  )

  const optionalExtrasNamesAndQuantitiesFromActivityVoucher =
    determineOptionalExtrasFromActivityVoucher({
      bookingFromActivityVoucher,
      optionalExtraPresenter
    })

  const currency = outlet.currency()

  const peopleGoing = adultsGoingQuantity + childrenGoingQuantity

  const optionalExtras = formatOptionalExtras(
    optionalExtraPresenter,
    desiredOptionalExtras
  )

  const noPeopleSelected = peopleGoing < 1
  const multiDay = activity.isMultiDay()
  const continuous = activity.continuous

  // Don't show warning text initially. As soon as people are selected enable it
  const [hasEnoughPeopleGoingWarning, setHasEnoughPeopleGoingWarning] =
    useState(false)
  if (peopleGoing > 0 && !hasEnoughPeopleGoingWarning)
    setHasEnoughPeopleGoingWarning(true)

  // Default minimum capacity is also 1 in DB
  const minimumCapacity = activity?.minimum_capacity ?? 1

  const maximumCapacity = activity?.maximum_capacity

  const maxAttendeesReached = maximumCapacity
    ? !!(peopleGoing >= maximumCapacity)
    : null

  // Only verify the minimum capacity for private bookings, after which
  // maximum capacity is controlled by the PeopleGoing selector
  const notAllowedToBook =
    !!activity?.private_activity && peopleGoing < minimumCapacity

  const { data: calendarData, loading: calendarDataLoading } = useQuery<{
    materialized_bookability_by_days: Materialized_Bookability_By_Days[]
  }>(FETCH_CALENDAR_AVAILABILITY, {
    variables: {
      schedulableId: activity.id,
      maxAvailability: peopleGoing
    },
    skip: ON_SERVER,
    ssr: ON_SERVER
  })

  const availableDatesForCalendar = (
    calendarData?.materialized_bookability_by_days || []
  ).map(({ on_date }) => new Date(definedNotNull(on_date)))

  const earliestDateForCalendar =
    availableDatesForCalendar.length > 0
      ? new Date(
          Math.min(...availableDatesForCalendar.map(date => date.getTime()))
        )
      : undefined

  const calendarSelectedAndDateTimeLoaded =
    !dateTimeError && !dateTimeLoading && !!calendarSelection

  useActivityValues({
    availableDatesForCalendar,
    loadingData: calendarDataLoading,
    activity
  })

  const { addToBasket, addToBasketLoading, addToBasketError } = useAddToBasket({
    chargeable: 'activity'
  })

  const [
    redeemActivityVoucher,
    { loading: redeemVoucherLoading, error: redeemVoucherError }
  ] = useMutation<
    Pick<Mutation_Root, 'Consumer_Baskets_RedeemActivityVoucher'>
  >(ACTIVITY_REVIEW_REDEEM_ACTIVITY_VOUCHER, {
    onCompleted: () => {
      redeemActivityVoucherOnComplete()
    },
    onError: () => {
      setActiveModalId(MODAL_IDS.error)
    }
  })

  const { activeModal, setActiveModalId, currentModalProps } =
    useModal<ModalProps>({
      MODAL_CONFIG,
      modalPropVariations: {
        calendar: {
          name: 'calendar',
          availableDays: availableDatesForCalendar,
          initialMonth: calendarSelection
            ? calendarSelection.utc().toDate()
            : earliestDateForCalendar,
          onDateChange: () => setActiveModalId(null),
          start: currentDate.toDate()
        },
        activeVoucher: {
          lineItemId: activityVoucherLineItemId,
          activityVoucherCode: code,
          basketPriceModifier: basketPriceModifiers ?? {},
          basketId: basket?.id,
          refetchBasket,
          onClose: () => {
            setActiveModalId(null)
          },
          changeViewOnSuccess: () => {
            setActiveModalId(null)
          }
        },
        addToBasket: {},
        error: {
          onClose: () => {
            setActiveModalId(null)
          },
          errorMessage: addToBasketError?.message || redeemVoucherError?.message
        }
      }
    })

  const standardBooking = !bookingFromActivityVoucher && !isGift

  const shouldBlockOnOrganizerParticipating =
    activity.adultsAllowed() && standardBooking
  const configIncomplete =
    peopleGoing === 0 ||
    (maximumCapacity && peopleGoing > maximumCapacity) ||
    (!timeSlot.id && !isGift) ||
    (shouldBlockOnOrganizerParticipating && !organizerParticipating)

  const totalAvailableTimeSlots =
    timeSlotBookings && Object.values(timeSlotBookings).length
  const timeSlotShowingCount =
    totalAvailableTimeSlots &&
    Math.min(totalAvailableTimeSlots, visibleTimeSlots)

  const { adultsPrice, childrenPrice } =
    activity.type === SchedulableTypeEnum.Activity
      ? // TODO #6362 - Fix ESLint - hook
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useGetPriceForBookingActivity({
          activity
        })
      : { adultsPrice: 0, childrenPrice: 0 }

  const onContinueClick = () => {
    if (configIncomplete) {
      if (
        peopleGoing === 0 ||
        (maximumCapacity && peopleGoing > maximumCapacity)
      ) {
        participantsFieldHolder?.current?.scrollIntoView({
          behavior: 'smooth'
        })
      } else if (!timeSlot.id && !isGift) {
        dateFieldHolder?.current?.scrollIntoView({
          behavior: 'smooth'
        })
      } else if (
        shouldBlockOnOrganizerParticipating &&
        !organizerParticipating
      ) {
        participatingFieldHolder?.current?.scrollIntoView({
          behavior: 'smooth'
        })
      }
    } else {
      !bookingFromActivityVoucher
        ? handleAddToBasketClick()
        : redeemActivityVoucherOnClick()
    }
  }

  const handleAddToBasketClick = async () => {
    if (!definedNotNull(activity.id) || (!timeSlot && !isGift)) {
      return
    }

    try {
      await addToBasket({
        variables: {
          schedulableId: activity?.id?.toString(),
          timeSlotGroupId: timeSlot?.id?.toString(),
          adultsGoing: adultsGoing.toString(),
          isGift,
          childrenGoing: childrenGoing.toString(),
          optionalExtrasAndQuantities: chosenOptionalExtrasInputs,
          organizerParticipating: organizerParticipating || false,
          exclusive,
          widgetSlug
        },
        onSuccess: () => {
          trackEvent('Basket_conversion', {
            item_type: 'activity',
            item_id: activity?.id,
            outlet_id: outlet?.id,
            widget: widgetSlug,
            enthusiast_logged_in: authenticated
          })
          shoppingBasketEnabled
            ? setActiveModalId(MODAL_IDS.addToBasket)
            : push(widgetRoutes.CHECKOUT.url({ widgetSlug }))
        },
        onError: () => setActiveModalId(MODAL_IDS.error)
      })
    } catch (err) {
      /**
       * Error states are handled in apollo and the useAddToBasket hook so nothing needs to be
       * actioned here. We are using try/catch simply to ensure the modal only opens on success
       */
    }
  }

  const redeemActivityVoucherOnComplete = () => {
    refetchBasket({ basketId: basket?.id })
    shoppingBasketEnabled
      ? setActiveModalId(MODAL_IDS.addToBasket)
      : push(widgetRoutes.CHECKOUT.url({ widgetSlug }))
  }

  const redeemActivityVoucherOnClick = async () => {
    try {
      await redeemActivityVoucher({
        variables: {
          basketId: basket?.id,
          timeSlotGroupId: timeSlot?.id?.toString(),
          activityVoucherId: activityVoucherId,
          widgetSlug
        }
      })
    } catch (err) {
      /**
       * Error states are handled in apollo and the useAddToBasket hook so nothing needs to be
       * actioned here. We are using try/catch simply to ensure the modal only opens on success
       */
    }
  }

  return (
    <>
      <Head>
        <title>{`${t('common:book')} - ${activity.name()} | eola`}</title>
      </Head>
      <Layout onScroll={handleScroll}>
        {/*
         * Header / Breadcrumb
         */}
        <Header />
        <BackBreadcrumb title={t('booking-config')} onClick={goToDetails} />
        <CoverImage
          mb={0}
          src={activity.imageURL('fullscreen')}
          height={60}
          variant="hero"
        />
        <StyledMain id="main">
          {!bookingFromActivityVoucher && (
            <>
              {!activity.members_only && activity.activity_vouchers_enabled && (
                <StyledContentCheckbox name={'isGift'} id="is-gift-purchase">
                  <label htmlFor="is-gift-purchase">
                    {t('purchase-as-gift')}
                  </label>
                </StyledContentCheckbox>
              )}

              {/*
               * Attendants
               */}
              <FieldHolder mb={4} hasValues={peopleGoing > 0} withBoxShadow>
                <span ref={participantsFieldHolder}></span>
                <FieldTitle required>{t('how-many-going')}</FieldTitle>
                {activity.adultsAllowed() && (
                  <PersonGoingInput
                    gaid="participants-count"
                    name="adultsGoing"
                    type="adults"
                    currency={currency}
                    pence={adultsPrice}
                    max={calculateMaxAttendants(
                      'adults',
                      {
                        adults: values.adultsGoing,
                        children: values.childrenGoing
                      },
                      activity
                    )}
                    incrementInactive={maxAttendeesReached}
                  />
                )}
                {activity.childrenAllowed() && (
                  <PersonGoingInput
                    gaid="participants-count"
                    name="childrenGoing"
                    type="children"
                    currency={currency}
                    pence={childrenPrice}
                    maxChildAge={outlet.child_age}
                    max={calculateMaxAttendants(
                      'children',
                      {
                        adults: values.adultsGoing,
                        children: values.childrenGoing
                      },
                      activity
                    )}
                    incrementInactive={maxAttendeesReached}
                  />
                )}
                {activity.allow_exclusive_bookings && (
                  <>
                    <Divider mb={4} />
                    <StyledContentCheckbox
                      name="exclusive"
                      disabled={
                        !activity.hasEnoughPeopleGoingForExclusive(peopleGoing)
                      }
                      id="is-exclusive"
                    >
                      <Flex flexDirection="column">
                        <Flex alignItems="center">
                          <Icon name="multiple-users" size={24} mr={2} />
                          <label htmlFor="is-exclusive">
                            {t('chargeable:make-booking-exclusive')}
                          </label>
                        </Flex>
                        <Text fontStyle="p3" mt={1}>
                          {t('chargeable:minimum-party-size', {
                            minimum: activity.minimum_party_size_for_exclusive
                          })}
                        </Text>
                      </Flex>
                    </StyledContentCheckbox>
                  </>
                )}
              </FieldHolder>

              {notAllowedToBook && hasEnoughPeopleGoingWarning && (
                <ValidationError>
                  {t('select-number-of-people-going', {
                    count: minimumCapacity
                  })}
                </ValidationError>
              )}
            </>
          )}
          {bookingFromActivityVoucher && (
            <GiftSummary
              adultsGoing={adultsGoingQuantity}
              childrenGoing={childrenGoingQuantity}
              optionalExtras={
                optionalExtrasNamesAndQuantitiesFromActivityVoucher
              }
            />
          )}

          {maximumCapacity && peopleGoing === maximumCapacity && (
            <Text size="xs" color="secondary">
              {t('max-attendees', { count: maximumCapacity })}
            </Text>
          )}

          {/*
           * We show the message in red for users who type in the number rather than using the increment buttons
           */}

          {maximumCapacity && peopleGoing > maximumCapacity && (
            <ValidationError>
              {t('max-attendees', { count: maximumCapacity })}
            </ValidationError>
          )}

          {/**
           * Date selector
           */}
          {!isGift && (
            <FieldHolder
              hasValues={!!calendarSelection}
              mb={4}
              disabled={noPeopleSelected || notAllowedToBook}
              withBoxShadow
            >
              <span ref={dateFieldHolder}></span>
              <FieldTitle
                required
                disabled={noPeopleSelected || notAllowedToBook}
              >
                {multiDay ? t('choose-start-date') : t('choose-date')}
              </FieldTitle>
              <ModalField
                gaid="date-select-modal"
                onClick={() => setActiveModalId(MODAL_CONFIG.calendar.id)}
                hasValues={!!calendarSelection}
                disabled={noPeopleSelected || notAllowedToBook}
              >
                <Text fontStyle={'h6'}>
                  {calendarSelection
                    ? calendarSelection.format('ddd D MMMM') +
                      (multiDay ? ` (${activity.formatDurationText(t)})` : '')
                    : t('choose-date-here')}
                </Text>
              </ModalField>
              {calendarSelection && <Divider variant="dashed" mt={5} />}
              {calendarSelection && dateTimeLoading && (
                <Spinner centered={true} />
              )}

              {/*
               * TimeSlots
               */}
              {calendarSelectedAndDateTimeLoaded && timeSlotBookings && (
                <>
                  <FieldTitle required mt={5}>
                    {t('choose-time-slot')}
                  </FieldTitle>
                  {Object.values(timeSlotBookings)
                    .slice(0, visibleTimeSlots)
                    .map(dateTime => {
                      const dateTimeId = definedNotNull(dateTime.id)
                      const spacesLeft = spacesLeftForTimeSlot(
                        dateTime.capacity_remaining,
                        dateTime.rhys_materialized_availability?.units
                      )
                      return (
                        <TimeSlot
                          gaid="timeslot"
                          key={dateTimeId}
                          name="timeSlot.id"
                          value={dateTimeId}
                          disabled={peopleGoing < 1 || peopleGoing > spacesLeft}
                        >
                          <TimeSlotContent>
                            {multiDay && !continuous && (
                              <Text fontStyle="p2" color="tertiary">
                                {t('each-day')}:
                              </Text>
                            )}
                            {continuous && (
                              <Text fontStyle="p2" color="tertiary">
                                {t('continuous')}:
                              </Text>
                            )}
                            <TimeSlotTag
                              content={[dateTime.timeStartToEnd()]}
                            />
                          </TimeSlotContent>
                          <TimeSlotContent>
                            {peopleGoing <= spacesLeft && (
                              <Text fontStyle="p2">
                                {t('common:available')}
                              </Text>
                            )}
                            {peopleGoing > spacesLeft && (
                              <Text fontStyle="p2">
                                {t('common:x-spaces-left', {
                                  count: spacesLeft
                                })}
                              </Text>
                            )}
                          </TimeSlotContent>
                        </TimeSlot>
                      )
                    })}
                  <Text
                    color="secondary"
                    textAlign="center"
                    fontStyle="p2"
                    mt={4}
                  >
                    {t('common:showing-x-out-of-y', {
                      count: timeSlotShowingCount,
                      total: totalAvailableTimeSlots
                    })}
                  </Text>
                  {!!(
                    totalAvailableTimeSlots &&
                    visibleTimeSlots < totalAvailableTimeSlots
                  ) && (
                    <StyledButton
                      mb={2}
                      mt={4}
                      variant="secondary"
                      onClick={showLaterTimeSlots}
                    >
                      {t('common:show-more')}
                    </StyledButton>
                  )}
                </>
              )}

              {dateTimeError && (
                <Text.Error role="alert">
                  {t('date-time-radio-error-text')}
                </Text.Error>
              )}

              {calendarSelectedAndDateTimeLoaded &&
                timeSlotBookings &&
                timeSlotBookings?.length < 1 && (
                  <Text paragraphMargins>{t('no-bookings-on-date')} </Text>
                )}

              {outlet.enable_requests_for_time_slots &&
                !activity.isMultiDay() && (
                  <>
                    <Divider variant="solid" mt={5} mb={5} />
                    <Text fontStyle="p2" textAlign="center" color="secondary">
                      {t('no-time-that-suits')}
                    </Text>
                    <StyledButtonWrapper>
                      <Button
                        onClick={goToRequest}
                        variant="tertiary"
                        mt={4}
                        gaid="request-a-time"
                      >
                        {t('request-a-time')}
                      </Button>
                    </StyledButtonWrapper>
                  </>
                )}
            </FieldHolder>
          )}

          {standardBooking && (
            <>
              {/*
               * Organizer Participating
               */}
              {activity.adultsAllowed() && (
                <FieldHolder
                  mb={4}
                  hasValues={!!organizerParticipating}
                  withBoxShadow
                >
                  <span ref={participatingFieldHolder}></span>
                  <FieldTitle required>{t('taking-part-yourself')}</FieldTitle>
                  <Flex>
                    <ContentRadio
                      gaid="organizer-participating"
                      name={'organizerParticipating'}
                      value={'true'}
                      id="participating-true"
                      mr={3}
                    >
                      <label htmlFor="participating-true">
                        {t('common:yes')}
                      </label>
                    </ContentRadio>
                    <ContentRadio
                      gaid="organizer-participating"
                      name={'organizerParticipating'}
                      value={'false'}
                      id="participating-false"
                    >
                      <label htmlFor="participating-false">
                        {t('common:no')}
                      </label>
                    </ContentRadio>
                  </Flex>
                </FieldHolder>
              )}
            </>
          )}
          {!bookingFromActivityVoucher && (
            <>
              {/*
               * Optional Extras / Add ons
               */}
              {optionalExtras && (
                <FieldHolder
                  mb={4}
                  hasValues={hasChosenFieldOptions(desiredOptionalExtras)}
                  withBoxShadow
                >
                  <span ref={optionalExtrasFieldHolder}></span>
                  <FieldTitle>{t('recommended-add-ons')}</FieldTitle>
                  {optionalExtras.map(item => {
                    return (
                      <FieldHolder hasValues={item.quantity > 0} key={item.id}>
                        <H6 mb={1}>{item.name}</H6>
                        <Price
                          pence={definedNotNull(item.price)}
                          currency={currency}
                          color="tertiary"
                        />
                        <FieldHolderQuantityRow>
                          <Text fontStyle="p3">{t('quantity')}:</Text>
                          <Quantity
                            gaid="optional-extra"
                            // Use lodash-like dot paths to reference nested Formik values
                            name={`optionalExtra.${item.id}`}
                            min={0}
                            max={50}
                          />
                        </FieldHolderQuantityRow>
                      </FieldHolder>
                    )
                  })}
                </FieldHolder>
              )}
            </>
          )}
          {showScrollButton && (
            <StyledFixButtonWrapper>
              <StyledFixButton
                variant="iconCircle"
                onClick={() => {
                  const participantsPosition =
                    participantsFieldHolder?.current?.getBoundingClientRect()?.y
                  const datePosition =
                    dateFieldHolder?.current?.getBoundingClientRect()?.y
                  const participatingPosition =
                    participatingFieldHolder?.current?.getBoundingClientRect()
                      ?.y
                  const optionalExtrasPosition =
                    optionalExtrasFieldHolder?.current?.getBoundingClientRect()
                      ?.y

                  if (
                    participantsPosition &&
                    currentScrollY < currentScrollY + participantsPosition
                  )
                    participantsFieldHolder?.current?.scrollIntoView({
                      behavior: 'smooth'
                    })
                  else if (
                    datePosition &&
                    currentScrollY < currentScrollY + datePosition
                  )
                    dateFieldHolder?.current?.scrollIntoView({
                      behavior: 'smooth'
                    })
                  else if (
                    participatingPosition &&
                    currentScrollY < currentScrollY + participatingPosition
                  )
                    participatingFieldHolder?.current?.scrollIntoView({
                      behavior: 'smooth'
                    })
                  else if (
                    optionalExtrasPosition &&
                    currentScrollY < currentScrollY + optionalExtrasPosition
                  )
                    optionalExtrasFieldHolder?.current?.scrollIntoView({
                      behavior: 'smooth'
                    })
                }}
                aria-label={t('next-step')}
              >
                <AnimatedIcon autoplay loop name="arrow" size={30} mx="auto" />
              </StyledFixButton>
            </StyledFixButtonWrapper>
          )}
        </StyledMain>

        {/*
         * BottomBar
         */}
        <BottomBar padding={0}>
          <VoucherBar
            activity={{ isActive: !!bookingFromActivityVoucher }}
            credit={{ data: creditVoucher }}
            discount={{ data: discountCode }}
            onClick={() => setActiveModalId(MODAL_IDS.activeVoucher)}
          />
          <BottomBarContainer>
            <Summary
              topText={() => (
                <SchedulablePrice
                  preAdjustmentsPence={finalPricePence}
                  pence={totalPrice}
                  currency={currency}
                  loading={totalPriceLoading}
                  aria-label={t('common:total')}
                />
              )}
              bottomText={t(`for-x-people`, { count: peopleGoing })}
            />
            <Button
              onClick={onContinueClick}
              inactive={addToBasketLoading || redeemVoucherLoading}
              loading={addToBasketLoading || redeemVoucherLoading}
            >
              {configIncomplete
                ? t('common:next')
                : shoppingBasketEnabled
                  ? t('add-to-basket')
                  : t('common:checkout')}
            </Button>
          </BottomBarContainer>
        </BottomBar>
      </Layout>
      <ActivityConfigModals activeModal={activeModal} {...currentModalProps} />
    </>
  )
}

export default ActivityConfig

const spacesLeftForTimeSlot = (
  capacityRemaining: RecursivePartial<Maybe<number> | undefined>,
  units: RecursivePartial<Maybe<number> | undefined>
) =>
  units
    ? Math.min(definedNotNull(capacityRemaining), units)
    : definedNotNull(capacityRemaining)

export const FETCH_ACTIVITY_TIMES = gql`
  query WidgetActivityConfigFetchActivityTimes(
    $schedulableId: uuid
    $from: timestamp
    $to: timestamp
  ) {
    time_slot_bookings(
      where: {
        privately_booked: { _eq: false }
        exclusively_booked: { _eq: false }
        disable_further_bookings: { _eq: false }
        schedulable_id: { _eq: $schedulableId }
        satisfies_notice_hours_override: { _eq: true }
        satisfies_outlet_notice_hours: { _eq: true }
        satisfies_outlet_notice_hours_when_underbooked: { _eq: true }
        satisfies_schedulable_notice_hours: { _eq: true }
        satisfies_schedulable_notice_hours_when_underbooked: { _eq: true }
        schedulable_state: { _eq: "active" }
        start: { _gte: $from, _lte: $to }
        capacity_remaining: { _gt: 0 }
        _or: [
          { _not: { rhys_materialized_availability: {} } }
          { rhys_materialized_availability: { units: { _gt: 0 } } }
        ]
      }
      order_by: { start: asc }
    ) {
      id
      start
      finish
      capacity_remaining
      rhys_materialized_availability {
        units
      }
    }
  }
`
const FETCH_CALENDAR_AVAILABILITY = gql`
  query WidgetActivityConfigFetchCalendarAvailability(
    $schedulableId: uuid
    $maxAvailability: Int
  ) {
    materialized_bookability_by_days(
      distinct_on: on_date
      where: {
        schedulable: { id: { _eq: $schedulableId } }
        max_availability: { _gte: $maxAvailability }
      }
    ) {
      on_date
    }
  }
`

export const ACTIVITY_REVIEW_REDEEM_ACTIVITY_VOUCHER = gql`
  mutation WidgetRedeemActivityVoucher(
    $basketId: ID!
    $timeSlotGroupId: ID!
    $activityVoucherId: ID!
    $enthusiastName: String
    $enthusiastEmail: String
    $contactNumber: String
    $widgetSlug: String!
  ) {
    Consumer_Baskets_RedeemActivityVoucher(
      basket_id: $basketId
      time_slot_group_id: $timeSlotGroupId
      activity_voucher_id: $activityVoucherId
      enthusiast_name: $enthusiastName
      enthusiast_email: $enthusiastEmail
      contact_number: $contactNumber
      widget_slug: $widgetSlug
    ) {
      basket_id
      price_after_adjustments_pence
    }
  }
`

const StyledButtonWrapper = styled.div(() => ({
  textAlign: 'center' as const,
  a: {
    margin: 0
  }
}))

const StyledContentCheckbox = styled(ContentCheckbox)(({ theme }) => ({
  ...theme.fontStyle.h6,
  marginBottom: theme.space[4]
}))

const StyledButton = styled(Button)(() => ({
  width: '100%'
}))

const StyledMain = styled.main(({ theme }) => ({
  marginTop: theme.space[4],
  counterReset: 'section',
  [`${FieldTitle}`]: {
    '&::before': {
      counterIncrement: 'section',
      content: `counter(section) ". "`
    }
  }
}))

const StyledFixButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.space[6],
  marginBottom: theme.space[9],
  backgroundColor: theme.colors.white,
  border: `2px solid ${rgba(theme.colors.primary, 0.3)} `,
  '&:hover': {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.primary
  },
  '&:active': {
    border: `2px solid ${rgba(theme.colors.primary, 0.3)} `
  },
  filter: `drop-shadow(0px 24px 34px ${rgba(theme.colors.shadow, 0.04)})`
}))

const StyledFixButtonWrapper = styled.div(() => ({
  position: 'relative' as const,
  display: 'flex',
  justifyContent: 'center'
}))
